import { Component, ElementRef, NgZone, OnInit, ViewChild, ChangeDetectorRef, TrackByFunction } from '@angular/core';
import { SelectControlValueAccessor } from '@angular/forms';
import { take } from 'rxjs/operators';
import { FormlyType } from '../ga-formly.service';
import { ExtraFieldType } from '../extra-field-type';
import { compareFn, GaFormlyUtil, trackByFunction } from '../ga-formly.util';

@Component({
  selector: 'formly-field-select',
  template: `
    <select 
      class="form-select form-select-sm" 
      [ngClass]="classNames" 
      [attr.x-autocompletetype]="to.autoCompleteType" 
      [attr.autocompletetype]="to.autoCompleteType" 
      [formControl]="formControl" 
      [formlyAttributes]="field" 
      [compareWith]="to.compareWith"
      [inputFocus]="to.inputFocus" #select>
      
      <option [ngValue]="undefined" *ngIf="!to.required || (to.required && !formControl.value) || !to._flatOptions" [defaultSelected]="true">{{ to.placeholder }}</option>
      <ng-container *ngIf="to.options | formlySelectOptions: field | async as opts">
        <ng-container *ngIf="to._flatOptions; else grouplist">
          <ng-container *ngFor="let opt of opts; trackBy: trackByFunction">
            <option [ngValue]="opt.value" [attr.disabled]="opt.disabled ? '' : null">{{opt.label | translate}}</option>
          </ng-container>
        </ng-container>
        
        <ng-template #grouplist>
          <ng-container *ngFor="let opt of opts; trackBy: trackByFunction">
            <option *ngIf="!opt.group; else optgroup" [ngValue]="opt.value" [attr.disabled]="opt.disabled ? '' : null">{{opt.label | translate}}</option>
            <ng-template #optgroup>
              <optgroup [label]="opt.label">
                <option *ngFor="let child of opt.group; trackBy: trackByFunction" [ngValue]="child.value" [attr.disabled]="child.disabled ? '' : null">{{child.label | translate}}</option>
              </optgroup>
            </ng-template>
          </ng-container>
        </ng-template>
      </ng-container>
      
    </select>
  `
})
export class FormlyFieldSelectComponent extends ExtraFieldType implements OnInit {
  public defaultOptions = {
    templateOptions: {
      options: []
    }
  };
  public jSelect: JQuery<HTMLSelectElement>;
  public trackByFunction: TrackByFunction<any>;
  @ViewChild('select', { static: true }) public selectElementRef: ElementRef;

  // workaround for https://github.com/angular/angular/issues/10010
  @ViewChild(SelectControlValueAccessor, { static: false }) set selectAccessor(s:any) {
    if (!s) {
      return;
    }

    const writeValue = s.writeValue.bind(s);
    if (s._getOptionId(s.value) === null) {
      writeValue(s.value);
    }

    s.writeValue = (value:any) => {
      const id = s._idCounter;
      writeValue(value);
      if (value === null) {
        this.ngZone.onStable.asObservable().pipe(take(1)).subscribe(() => {
          if (id !== s._idCounter && s._getOptionId(value) === null && s._elementRef.nativeElement.selectedIndex !== -1) {
            writeValue(value);
          }
        });
      }
    };
  }

  constructor(
    private ngZone:NgZone,
    private changeDetectorRef:ChangeDetectorRef,
    private formlyUtil: GaFormlyUtil
  ) {
    super();
  }

  public ngOnInit():void {
    this.to.compareWith = compareFn.bind(this);
    this.trackByFunction = trackByFunction.bind(this);
    this.formlyUtil.initSelectField(this.to, this.formControl);
    this.classNames = this.formlyUtil.createClassNames(FormlyType.SELECT, <string>this.key, this.to.label);
    this.jSelect = $(this.selectElementRef.nativeElement);

    // event initial value, options and model change
    this.formlyUtil.onOptionsAndModelChange(this.jSelect, this.to, this.formControl);

    // register listener
    this.listen();
  }

  public ngAfterViewInit():void {
    this.changeDetectorRef.detectChanges();
  }

  protected listen(): void {
    super.listen();

    // get common listener for select field
    this.formlyUtil.getSelectListener(this.to, this.formControl, this.jSelect);
  }
}